/**
 * The administration surface: `/api/admin/master-data/*`,
 * `/api/admin/validation-rules`, `/api/admin/roles` and
 * `/api/admin/operations/backups` (Requirements 16, 8.9, 1.9, 17.6, 17.10).
 *
 * Three things these schemas make structural rather than procedural:
 *
 * - **The status set is not master data.** R16.10 permits the display label and
 *   display order only, so there is no create, delete or deactivate shape for it
 *   anywhere in this package. The absence is the enforcement.
 * - **Only configurable rules are writable.** R8.9 withholds the hard rules of R8.3
 *   from configuration, so {@link validationRuleUpdateSchema} carries a rule
 *   identifier, a tier, a message and an active flag - and the tier it accepts
 *   excludes `hard`, because promoting a rule into the hard tier would let
 *   configuration create a blocking rule the requirement does not permit.
 * - **A master data record's shape is per set.** A Platform carries a colour, a
 *   priority carries the elevated flag, a milestone type carries the mandatory rule
 *   and the calendar-relevance flag; the rest share the common shape (design.md,
 *   "Conventions").
 */
import { z } from 'zod';
import { canonicalizeColorOrKeep } from '@complan/domain';
import { comPlanRoleSchema, masterDataSetSchema, runOutcomeSchema, validationTierSchema, } from './enums.js';
import { MASTER_DATA_CODE_MAX_LENGTH, MASTER_DATA_NAME_MAX_LENGTH, MASTER_DATA_NAME_MIN_LENGTH, VALIDATION_MESSAGE_MAX_LENGTH, VALIDATION_MESSAGE_MIN_LENGTH, } from './limits.js';
import { idSchema, pagedResponseSchema, planningDateSchema, ruleIdSchema, textSchema, timestampSchema, } from './primitives.js';
/** `/api/admin/master-data/:set`. */
export const masterDataSetParamsSchema = z.object({ set: masterDataSetSchema });
/**
 * The fixed value set whose display label and display order alone are
 * configurable (R16.10): the Initiative status set. Used as the path segment of
 * `/api/admin/status-finality/:kind`.
 *
 * There is no create, delete or deactivate shape for it anywhere in this package
 * - R16.10 permits neither, and the absence is the enforcement, the same way the
 * status set is an enum rather than a master data table.
 */
export const displayValueKindSchema = z.enum(['status']);
/** `/api/admin/status-finality/:kind`. */
export const displayValueKindParamsSchema = z.object({ kind: displayValueKindSchema });
/** `/api/admin/status-finality/:kind/:value`. */
export const displayValueParamsSchema = displayValueKindParamsSchema.extend({
    value: z.string().min(1),
});
/**
 * The only editable attributes of a fixed status value (R16.10): the
 * display label (1 to 100 characters) and the display order. The value itself is
 * fixed and is not part of the write shape, so a request cannot rename, add or
 * remove a member of either set through this schema.
 */
export const displayValueUpdateSchema = z.object({
    displayLabel: textSchema(MASTER_DATA_NAME_MIN_LENGTH, MASTER_DATA_NAME_MAX_LENGTH),
    displayOrder: z.number().int().min(0),
});
/** A status value as read back: the fixed value plus its editable display attributes (R16.10). */
export const displayValueSchema = displayValueUpdateSchema.extend({
    value: z.string().min(1),
});
/** `GET /api/admin/status-finality/:kind`. */
export const displayValueListResponseSchema = z.object({
    kind: displayValueKindSchema,
    items: z.array(displayValueSchema).default([]),
});
/** `/api/admin/master-data/:set/:id`. */
export const masterDataRecordParamsSchema = masterDataSetParamsSchema.extend({ id: idSchema });
/**
 * The shape every master data set shares: `code`, `name` of 1 to 100 characters
 * unique among active records, display order, effective-from date and active state
 * (R16.8, R16.11).
 */
export const masterDataWriteSchema = z.object({
    code: textSchema(1, MASTER_DATA_CODE_MAX_LENGTH),
    name: textSchema(MASTER_DATA_NAME_MIN_LENGTH, MASTER_DATA_NAME_MAX_LENGTH),
    displayOrder: z.number().int().min(0),
    effectiveFrom: planningDateSchema,
    isActive: z.boolean().default(true),
});
/**
 * A Platform, which additionally carries the bar colour (R16.1).
 *
 * The colour rules of R16.12 - 4.5:1 against the Calendar_View background,
 * uniqueness among active Platforms, and the configured active-Platform maximum -
 * are computed rules with findings, evaluated in the domain against the current
 * Platform set. The schema states only that a colour is present.
 */
export const platformWriteSchema = masterDataWriteSchema.extend({
    /**
     * The bar colour, canonicalised on the way in (R16.12).
     *
     * Stored as lowercase `#rrggbb` and compared as a plain string, so `#FB7702`
     * and `#fb7702` must not both be storable - see `HexColor`'s note in
     * `@complan/domain`. Rather than make an administrator retype a colour their
     * design tool or the OS colour picker handed them in a different notation, the
     * schema reduces `#RRGGBB`, `#rgb`, a bare `RRGGBB` and `rgb(r, g, b)` to that
     * one spelling here, at the edge.
     *
     * A value that names no colour at all is passed through untouched, so
     * `validatePlatformColor` still reports it as the `malformed_color` finding that
     * tells the administrator which field is wrong - rather than this schema
     * rejecting it as a field issue, or substituting a colour nobody chose.
     */
    colour: z.string().min(1).transform(canonicalizeColorOrKeep),
});
/** A priority, which carries the Elevated_Priority_Flag the calendar marker reads (R16.3, R10.8). */
export const priorityWriteSchema = masterDataWriteSchema.extend({
    elevatedPriorityFlag: z.boolean().default(false),
});
/**
 * A milestone type, which carries the mandatory rule, the Calendar_Relevance_Flag
 * and the preceding type the ordering warning of R8.6 compares against (R16.3).
 */
export const milestoneTypeWriteSchema = masterDataWriteSchema.extend({
    calendarRelevanceFlag: z.boolean().default(false),
    isMandatory: z.boolean().default(false),
    precedingMilestoneTypeId: idSchema.nullable().default(null),
});
/**
 * A POS_Segment, which additionally carries the OneData-sourced impacted POS
 * count and its source timestamp (`complan-manpower/requirements.md`
 * Requirement 4.2, Assumption A8).
 *
 * `posCount` is writable here because a System_Administrator's manual import
 * path (Assumption A8's interim, non-live path) is exactly a
 * Master_Data_Administration edit of this set - there is no separate import
 * endpoint in Manpower's scope. `sourceTimestamp` defaults to `null` rather
 * than to the current time: a freshly created segment with no import behind
 * it yet is meaningfully different from one whose count has a known source
 * date, and only an actual import (or an administrator entering one
 * deliberately) should set it.
 */
export const posSegmentWriteSchema = masterDataWriteSchema.extend({
    posCount: z.number().int().min(0).default(0),
    sourceTimestamp: timestampSchema.nullable().default(null),
});
/**
 * A master data record as read back: the common shape plus the per-set fields, and
 * the reference count R16.6 reports when a deletion is refused.
 */
export const masterDataRecordSchema = masterDataWriteSchema.extend({
    id: idSchema,
    set: masterDataSetSchema,
    colour: z.string().min(1).nullable().default(null),
    elevatedPriorityFlag: z.boolean().nullable().default(null),
    calendarRelevanceFlag: z.boolean().nullable().default(null),
    isMandatory: z.boolean().nullable().default(null),
    precedingMilestoneTypeId: idSchema.nullable().default(null),
    posCount: z.number().int().min(0).nullable().default(null),
    sourceTimestamp: timestampSchema.nullable().default(null),
    /** How many Initiatives reference this record; a deletion is refused while it is above zero (R16.6). */
    referencingInitiativeCount: z.number().int().min(0),
});
/** `GET /api/admin/master-data/:set`. */
export const masterDataListResponseSchema = z.object({
    set: masterDataSetSchema,
    items: z.array(masterDataRecordSchema).default([]),
});
/**
 * A configurable validation rule as read back (R8.9).
 *
 * `configurable` is `false` for the hard rules of R8.3, which are listed so
 * Master_Data_Administration can render them as fixed rather than pretend they do
 * not exist.
 */
export const validationRuleConfigSchema = z.object({
    ruleId: ruleIdSchema,
    tier: validationTierSchema,
    message: textSchema(VALIDATION_MESSAGE_MIN_LENGTH, VALIDATION_MESSAGE_MAX_LENGTH),
    isActive: z.boolean(),
    configurable: z.boolean(),
});
/**
 * One rule change. The tier excludes `hard`: R8.9 permits governance, warning or
 * information, and configuration must not be able to create a blocking rule.
 */
export const validationRuleUpdateSchema = z.object({
    ruleId: ruleIdSchema,
    tier: z.enum(['governance', 'warning', 'information']),
    message: textSchema(VALIDATION_MESSAGE_MIN_LENGTH, VALIDATION_MESSAGE_MAX_LENGTH),
    isActive: z.boolean(),
});
/** `PUT /api/admin/validation-rules`. */
export const updateValidationRulesRequestSchema = z.object({
    rules: z.array(validationRuleUpdateSchema).min(1),
});
/** `GET /api/admin/validation-rules`. */
export const validationRulesResponseSchema = z.object({
    rules: z.array(validationRuleConfigSchema).default([]),
});
/**
 * A user's role assignment as submitted on a write (R1.7-R1.9).
 *
 * The scopes are part of the assignment because R2.4 defines Platform Owner and
 * Channel Owner read scope by Identity_Service group membership, and the request
 * pipeline binds exactly these arrays to the RLS session variables. Carries no
 * display name: a `PUT` replaces the role/scope assignment, never the identity
 * projection the Identity_Service owns, so there is nothing name-shaped for a
 * caller to submit.
 */
export const roleAssignmentSchema = z.object({
    userId: idSchema,
    roles: z.array(comPlanRoleSchema).default([]),
    approverRights: z.boolean(),
    platformScope: z.array(idSchema).default([]),
    channelScope: z.array(idSchema).default([]),
});
/**
 * `PUT /api/admin/roles`. A whole-assignment replace, so the audit entry records the
 * previous and the new role set in one event (R1.9).
 *
 * The change takes effect on the affected user's next request, without a sign-out,
 * because roles are resolved per request rather than from a claim cached at login
 * (R1.10).
 */
export const updateRoleAssignmentRequestSchema = roleAssignmentSchema;
/**
 * A user's role assignment as read back, {@link roleAssignmentSchema} plus the
 * display name resolved server-side (R1.7-R1.9) - so the administration table
 * shows the person's name rather than their `app_user` id.
 */
export const roleAssignmentViewSchema = roleAssignmentSchema.extend({
    userDisplayName: z.string().min(1),
});
/** `GET /api/admin/roles`. */
export const roleAssignmentListResponseSchema = pagedResponseSchema(roleAssignmentViewSchema);
/** One recorded backup run (R17.6). */
export const backupRunSchema = z.object({
    id: idSchema,
    scheduledAt: timestampSchema,
    completedAt: timestampSchema.nullable().default(null),
    coveredStores: z.array(z.string().min(1)).default([]),
    outcome: runOutcomeSchema,
    initiativeCount: z.number().int().min(0).nullable().default(null),
    masterDataCount: z.number().int().min(0).nullable().default(null),
    changeEventCount: z.number().int().min(0).nullable().default(null),
    /** At least 30 days from completion; the window itself is configuration (R17.6). */
    retainUntil: timestampSchema,
});
/** One restore verification, comparing counts against the verified run (R17.10). */
export const restoreVerificationSchema = z.object({
    id: idSchema,
    backupRunId: idSchema,
    verifiedAt: timestampSchema,
    initiativeCount: z.number().int().min(0).nullable().default(null),
    masterDataCount: z.number().int().min(0).nullable().default(null),
    changeEventCount: z.number().int().min(0).nullable().default(null),
    outcome: runOutcomeSchema,
});
/** `GET /api/admin/operations/backups` (R17.6, R17.10). */
export const operationsBackupsResponseSchema = z.object({
    backupRuns: z.array(backupRunSchema).default([]),
    restoreVerifications: z.array(restoreVerificationSchema).default([]),
});
//# sourceMappingURL=admin.js.map